import { useEffect, useState } from "react";
import type { PublicFaqEntry } from "@fa/shared";
import { t as getFixedT } from "@/i18n";
import type { SupportedLanguage } from "@/lib/site";
import { getPublicFaq } from "@/api/publicApi";
import "./Faq.css";

export interface FaqItem {
  question: string;
  answer: string;
}

interface FaqProps {
  lang: SupportedLanguage;
}

/** #faq — 09-landing-page.md §2/§5.3 (H2 "الأسئلة الشائعة", H3 per question) and §7 "real
 * disclosure semantics (`<button aria-expanded>` controlling a linked panel), not divs with only
 * click handlers." Multiple panels may be open at once (no accordion-exclusivity requirement
 * stated) — each disclosure is independent.
 *
 * T141: the prerendered/no-JS baseline is the `faq.items` i18n copy (unchanged markup/semantics —
 * this is also the source `HomePage.tsx` uses for the prerendered `FAQPage` JSON-LD, deliberately
 * left as-is per Assumption D/DEC-WC4). On mount this section additionally client-fetches the live
 * `@Public` list (`GET /public/content/faq`, both languages per row) and swaps its *displayed*
 * disclosure list to it once the fetch resolves non-empty, picking the active `lang` client-side.
 * Loading, failure, or an empty result all keep the i18n fallback rendered — the section is never
 * empty, and the JSON-LD block (built separately, from i18n, in `HomePage.tsx`) is unaffected.
 */
export function Faq({ lang }: FaqProps) {
  const t = getFixedT(lang);
  const fallbackItems = t("faq.items", { returnObjects: true }) as FaqItem[];

  const [liveEntries, setLiveEntries] = useState<PublicFaqEntry[]>([]);
  const [status, setStatus] = useState<"loading" | "loaded" | "error">(
    "loading",
  );

  useEffect(() => {
    let cancelled = false;
    getPublicFaq()
      .then((data) => {
        if (cancelled) return;
        setLiveEntries(data);
        setStatus("loaded");
      })
      .catch(() => {
        if (cancelled) return;
        setStatus("error");
      });
    // Mount-only fetch, same pattern as `LeadForm`'s grade/school lookups — a failure silently
    // keeps the i18n fallback (there is nothing for a visitor to "fix").
    return () => {
      cancelled = true;
    };
  }, []);

  // Live data only replaces the fallback once it has actually resolved with at least one row;
  // loading/error/empty all keep the prerendered i18n copy so the section is never empty.
  const items: FaqItem[] =
    status === "loaded" && liveEntries.length > 0
      ? liveEntries.map((entry) => ({
          question: lang === "ar" ? entry.questionAr : entry.questionEn,
          answer: lang === "ar" ? entry.answerAr : entry.answerEn,
        }))
      : fallbackItems;

  const [openIndexes, setOpenIndexes] = useState<Set<number>>(new Set());

  function toggle(index: number) {
    setOpenIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }

  return (
    <section id="faq" className="fa-faq" aria-labelledby="faq-heading">
      <div className="fa-section-inner">
        <h2 id="faq-heading" className="fa-section-heading">
          {t("faq.heading")}
        </h2>
        <div className="fa-faq__list">
          {items.map((item, index) => {
            const isOpen = openIndexes.has(index);
            const buttonId = `faq-question-${index}`;
            const panelId = `faq-answer-${index}`;
            return (
              <div className="fa-faq__item" key={index}>
                <h3 className="fa-faq__question-heading">
                  <button
                    type="button"
                    id={buttonId}
                    className="fa-faq__question"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => toggle(index)}
                  >
                    <span>{item.question}</span>
                    <span className="fa-faq__chevron" aria-hidden="true">
                      {isOpen ? "−" : "+"}
                    </span>
                  </button>
                </h3>
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={buttonId}
                  className="fa-faq__answer"
                  hidden={!isOpen}
                >
                  <p>{item.answer}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
